<template>
    <div class="mb-2">
        <!-- Dwell chip: parked time between this trip and the one before it -->
        <div v-if="dwellMinutes >= 5" class="d-flex align-center mb-1 ms-2">
            <div class="dwell-line me-2"></div>
            <v-chip size="x-small" variant="outlined" color="blue-grey"
                    prepend-icon="mdi-clock-outline" class="text-caption">
                Parked {{ formatDwell(dwellMinutes) }}
            </v-chip>
        </div>

        <v-card variant="outlined" class="activity-trip-card"
                :style="{ borderLeft: isLive ? '4px solid #4CAF50' : '4px solid #1976D2' }">
            <v-card-text class="pa-3">
                <!-- Header row -->
                <div class="d-flex align-center ga-2 mb-2">
                    <v-icon icon="mdi-truck" :color="isLive ? 'success' : 'primary'" size="18"/>
                    <span class="text-body-2 font-weight-bold text-truncate flex-grow-1 min-w-0">
                        {{ trip.asset || 'Vehicle' }}
                    </span>
                    <v-chip v-if="isLive" size="x-small" color="success" variant="flat"
                            prepend-icon="mdi-circle" class="font-weight-black text-uppercase"
                            style="letter-spacing: 0.05em;">Live</v-chip>
                    <v-chip v-if="trip.trip_score != null && !isLive" size="x-small" variant="tonal"
                            :color="scoreColor">
                        {{ formatTripScore(trip.trip_score) }}
                    </v-chip>
                    <span class="text-caption text-medium-emphasis flex-shrink-0">{{ startTime }}</span>
                </div>

                <!-- A→B route connector -->
                <div class="d-flex align-start ga-2 ps-1">
                    <div class="d-flex flex-column align-center pt-1" style="width: 14px;">
                        <v-icon icon="mdi-circle-medium" size="14" color="primary"/>
                        <div class="route-line"></div>
                        <v-icon :icon="isLive ? 'mdi-circle-outline' : 'mdi-map-marker'"
                                size="14" :color="isLive ? 'grey' : 'error'"/>
                    </div>
                    <div class="flex-grow-1 min-w-0">
                        <div class="text-caption text-truncate mb-3">
                            {{ trip.from || formatCoords(trip.start_location) || 'Starting point' }}
                        </div>
                        <div class="text-caption text-truncate" :class="isLive ? 'text-success' : ''">
                            {{ isLive ? 'In progress...' : (trip.to || formatCoords(trip.end_location) || 'Destination') }}
                        </div>
                    </div>
                </div>

                <!-- Stats + action row -->
                <div class="d-flex align-center ga-1 mt-3 flex-wrap">
                    <v-chip v-if="trip.distance" size="x-small" variant="tonal" color="primary">
                        <v-icon start icon="mdi-map-marker-distance" size="12"/>
                        {{ distanceText }}
                    </v-chip>
                    <v-chip v-if="trip.duration && !isLive" size="x-small" variant="tonal">
                        <v-icon start icon="mdi-timer-outline" size="12"/>
                        {{ durationText }}
                    </v-chip>
                    <v-chip v-if="trip.driver_name" size="x-small" variant="tonal">
                        <v-icon start icon="mdi-account" size="12"/>
                        {{ trip.driver_name }}
                    </v-chip>
                    <v-spacer/>
                    <v-btn size="x-small" variant="text" color="primary"
                           prepend-icon="mdi-map-marker-path" @click.stop="showRoute = true">
                        Route
                    </v-btn>
                </div>
            </v-card-text>
        </v-card>

        <v-dialog v-model="showRoute" max-width="800" scrollable>
            <v-card>
                <v-card-title class="d-flex align-center pa-3">
                    <span class="text-body-1 font-weight-bold">Trip Route</span>
                    <v-spacer/>
                    <v-btn icon="mdi-close" aria-label="Close" variant="text" size="small" @click="showRoute = false"/>
                </v-card-title>
                <v-card-text class="pa-0" style="height: 480px;">
                    <TripRouteMap v-if="showRoute && trip.asset_id"
                                  :selected-device="routeDevice"
                                  :selected-trip="trip"/>
                    <div v-else-if="showRoute" class="d-flex align-center justify-center pa-8 text-medium-emphasis">
                        Route map not available
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script setup>
import {ref, computed} from 'vue';
import {formatCompanyDateTime} from "@/composables/useTimezone";
import {strOperations} from "@/composables/globals";
import {useTripScore} from "@/composables/useTripScore";
import TripRouteMap from "@/components/cards/TripRouteMap.vue";

const props = defineProps({
    trip: {type: Object, required: true},
    dwellMinutes: {type: Number, default: 0},
});

const showRoute = ref(false);

const {formatTripScore} = useTripScore();

// Set by the feed from the vehicle's current trip
const isLive = computed(() => !!props.trip._live);

// Fixed for the card's life, so an open route map does not reload when the feed recomputes
const routeDevice = {asset_id: props.trip.asset_id, company: props.trip._company};

const startTime = computed(() => {
    if (!props.trip.time) return '';
    return formatCompanyDateTime(props.trip.time);
});

const distanceText = computed(() => {
    const km = parseFloat(props.trip.distance);
    if (!km) return null;
    return strOperations().unitifyDistance(km, 'distance', 'km');
});

const durationText = computed(() => {
    const secs = parseInt(props.trip.duration);
    if (!secs) return null;
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
});

const scoreColor = computed(() => {
    // Trip scores are on a 1-10 scale.
    const s = parseFloat(props.trip.trip_score);
    if (isNaN(s)) return 'grey';
    if (s >= 8) return 'success';
    if (s >= 5) return 'warning';
    return 'error';
});

const formatCoords = (loc) => {
    if (!loc?.lat) return null;
    return `${parseFloat(loc.lat).toFixed(4)}, ${parseFloat(loc.lon).toFixed(4)}`;
};

const formatDwell = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
};
</script>

<style scoped>
.min-w-0 {
    min-width: 0;
}
.activity-trip-card {
    border-radius: 8px;
    transition: background-color 0.15s;
}
.activity-trip-card:hover {
    background-color: rgba(var(--v-theme-on-surface), 0.03);
}
.route-line {
    width: 1px;
    height: 20px;
    background: rgba(var(--v-theme-on-surface), 0.25);
    margin: 2px 0;
}
.dwell-line {
    width: 1px;
    height: 14px;
    background: rgba(var(--v-theme-on-surface), 0.2);
}
</style>
